import React, { useEffect, useState, useCallback, useMemo } from "react";
import { personaDetailsAPI, deletePersonaAPI } from "../../../api/personaAPI";
import { useMsal } from "@azure/msal-react";
import styles from './persona.module.css';
import { CircularProgress, Box, ListItem } from "@mui/material";
import resetBtn from '../../../../assets/images/resetBtn.png';
import plusIcon from "../../../../assets/images/plusIcon.svg";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import PersonaTable from "./personaTable";
import AddAndEditPopup from "./addAndEditPersona";
import DeletePopup from "../User/deletePopup";

const Persona = ({ isSuperAdmin }) => {
    const adminType = 'Persona';
    const { accounts } = useMsal();
    const activeAccount = accounts[0];

    const [personaDetails, setPersonaDetails] = useState([]);
    const [originalPersonaAccess, setOriginalPersonaAccess] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPersona, setSelectedPersona] = useState(null);
    const [openAddPersona, setOpenAddPersona] = useState(false);
    const [singleDeletePopupOpen, setSingleDeletePopupOpen] = useState(false);
    const [selectedPersonaData, setSelectedPersonaData] = useState(null);
    const [actionType, setActionType] = useState('add');

    const [snackBarObject, setSnackBarObject] = useState({
        type: 'success',
        message: '',
        snackbarOpen: false
    });

    const showSnackbar = useCallback((type, message) => {
        setSnackBarObject({
            type,
            message,
            snackbarOpen: true
        });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackBarObject(prev => ({
            ...prev,
            snackbarOpen: false
        }));
    }, []);

    const fetchPersonaDetails = useCallback(async () => {
        if (!activeAccount) return;

        setIsLoading(true);
        const payload = {
            email: activeAccount.username,
            type: adminType,
            role: activeAccount?.idTokenClaims?.roles,
        };

        try {
            const personas = await personaDetailsAPI(payload);
            setPersonaDetails(personas);
            setOriginalPersonaAccess(personas.personaAccess || []);
        } catch (error) {
            console.error("Error fetching persona details:", error);
            showSnackbar('error', 'Failed to fetch persona details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [activeAccount, adminType, showSnackbar]);

    // Filtered persona access data based on selected filters
    const filteredPersonaAccess = useMemo(() => {
        if (!originalPersonaAccess.length || !personaDetails.filters) {
            return originalPersonaAccess;
        }

        // Check if any filters are actually selected
        const hasActiveFilters = personaDetails.filters.some(filter =>
            filter.selectedData && filter.selectedData.length > 0
        );

        // If no filters are selected, return original data without processing
        if (!hasActiveFilters) {
            return originalPersonaAccess;
        }

        // Only filter when there are active filters
        const filtered = originalPersonaAccess.filter(persona => {
            return personaDetails.filters.every(filter => {
                // Skip filters with no selected data
                if (!filter.selectedData || filter.selectedData.length === 0) {
                    return true;
                }

                // Apply filter based on label
                switch (filter.label) {
                    case 'Persona':
                        return filter.selectedData.includes(persona.Persona);
                    case 'Pages':
                        return filter.selectedData.includes(persona.Page);
                    case 'Access type':
                        return filter.selectedData.includes(persona['Access Type']);
                    default:
                        return true;
                }
            });
        });

        return filtered;
    }, [originalPersonaAccess, personaDetails.filters]);

    useEffect(() => {
        fetchPersonaDetails();
    }, [fetchPersonaDetails]);

    const handleMultiSelectChange = useCallback((label, selectedValues) => {
        setPersonaDetails(prevDetails => ({
            ...prevDetails,
            filters: prevDetails.filters.map(filter =>
                filter.label === label
                    ? { ...filter, selectedData: selectedValues }
                    : filter
            )
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setPersonaDetails(prevDetails => ({
            ...prevDetails,
            filters: prevDetails.filters.map(filter => ({
                ...filter,
                selectedData: []
            }))
        }));
    }, [showSnackbar]);

    const handleOpenAddPersona = useCallback(() => {
        setActionType('add');
        setSelectedPersonaData(null);
        setOpenAddPersona(true);
    }, []);

    const handleCloseAddPersona = useCallback(() => {
        setOpenAddPersona(false);
        setSelectedPersonaData(null);
        setActionType('add');
    }, []);

    const handleEditPersonaOpen = useCallback((personaData) => {
        setActionType('edit');
        setSelectedPersonaData(personaData);
        setOpenAddPersona(true);
    }, []);

    const handleDeletePersona = useCallback((personaData) => {
        setSelectedPersona(personaData.Persona);
        setSelectedPersonaData(personaData);
        setSingleDeletePopupOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        try {
            const personaId = selectedPersonaData?.ID;

            if (!personaId) {
                showSnackbar('error', 'No valid persona selected for deletion. Please check persona data.');
                return;
            }
            await deletePersonaAPI(personaId);
            setOriginalPersonaAccess(prevAccess =>
                prevAccess.filter(persona => persona.ID !== personaId)
            );

            setPersonaDetails(prevDetails => {
                if (!prevDetails.filters) return prevDetails;

                const remainingData = originalPersonaAccess.filter(persona => persona.ID !== personaId);

                const updatedFilters = prevDetails.filters.map(filter => {
                    switch (filter.label) {
                        case 'Persona':
                            const uniquePersonas = [...new Set(remainingData.map(item => item.Persona))];
                            return { ...filter, data: uniquePersonas };

                        default:
                            return filter;
                    }
                });

                return {
                    ...prevDetails,
                    filters: updatedFilters
                };
            });

            setSingleDeletePopupOpen(false);
            setSelectedPersona(null);
            setSelectedPersonaData(null);
            showSnackbar('success', `Page "${selectedPersonaData.Page}" deleted successfully from persona "${selectedPersonaData.Persona}"`);
        } catch (error) {
            console.error("Error deleting persona:", error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete persona. Please try again.';
            showSnackbar('error', errorMessage);
        }
    }, [selectedPersona, selectedPersonaData, showSnackbar, originalPersonaAccess]);

    const handleSavePersona = useCallback(async (personaData) => {
        try {
            const apiResponse = Array.isArray(personaData) ? personaData : personaData.apiResponse;

            if (apiResponse && Array.isArray(apiResponse)) {
                const newPersonaEntries = apiResponse.map(item => ({
                    ID: item.id,
                    Persona: item.persona,
                    Page: item.page,
                    'Access Type': item.access_type,
                    Scope: item.scope
                }));

                if (actionType === 'add') {
                    setOriginalPersonaAccess(prevAccess => [...prevAccess, ...newPersonaEntries]);
                } else if (actionType === 'edit') {
                    setOriginalPersonaAccess(prevAccess => {
                        const updatedAccess = [...prevAccess];
                        const personaName = newPersonaEntries[0]?.Persona;
                        const filteredAccess = updatedAccess.filter(
                            item => item.Persona !== personaName
                        );

                        return [...filteredAccess, ...newPersonaEntries];
                    });
                }

                setPersonaDetails(prevDetails => {
                    if (!prevDetails.filters) return prevDetails;
                    const updatedFilters = prevDetails.filters.map(filter => {
                        switch (filter.label) {
                            case 'Persona':
                                const newPersonas = newPersonaEntries.map(entry => entry.Persona);
                                const uniquePersonas = [...new Set([...(filter.data || []), ...newPersonas])];
                                return { ...filter, data: uniquePersonas };

                            case 'Pages':
                                const newPages = newPersonaEntries.map(entry => entry.Page);
                                const uniquePages = [...new Set([...(filter.data || []), ...newPages])];
                                return { ...filter, data: uniquePages };

                            case 'Access type':
                                const newAccessTypes = newPersonaEntries.map(entry => entry['Access Type']);
                                const uniqueAccessTypes = [...new Set([...(filter.data || []), ...newAccessTypes])];
                                return { ...filter, data: uniqueAccessTypes };

                            default:
                                return filter;
                        }
                    });

                    return {
                        ...prevDetails,
                        filters: updatedFilters
                    };
                });

                handleCloseAddPersona();
                const pageCount = newPersonaEntries.length;
                const personaName = newPersonaEntries[0]?.Persona || 'Persona';
                const message = actionType === 'add'
                    ? `Persona "${personaName}" added with ${pageCount} page(s) successfully`
                    : `Persona "${personaName}" updated successfully`;

                showSnackbar('success', message);
            } else {
                throw new Error('Invalid API response format');
            }
        } catch (error) {
            console.error("Error in handleSavePersona:", error);
            showSnackbar('error', 'An error occurred while saving. Please try again.');
        }
    }, [actionType, handleCloseAddPersona, showSnackbar]);

    const LoadingComponent = () => (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            flexDirection="column"
            gap={2}
        >
            <CircularProgress size={60} />
            <div style={{ fontSize: '16px', color: '#666' }}>
                Loading persona data...
            </div>
        </Box>
    );

    return (
        <div className={styles.adminContainer}>
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <>
                    <div className={styles.filters}>
                        {personaDetails?.filters?.filter(filter => filter.isFavourite)
                            .map(filter => (
                                <ListItem key={filter.label} className={styles.listCustomClass}>
                                    <MultiSelectWithSearchInput
                                        key={filter.label}
                                        label={filter.label}
                                        options={filter.data}
                                        filterValues={filter.selectedData}
                                        handleFilter={handleMultiSelectChange}
                                        disabled={false}
                                        type={adminType}
                                    />
                                </ListItem>
                            ))}
                        <div style={{ paddingLeft: '5px', display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                            <img
                                src={resetBtn}
                                onClick={resetFilters}
                                alt="Reset Filters"
                                style={{ cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    { isSuperAdmin && <div className={styles.btnContainer}>
                        <CustomButton
                            type="adminAddNew"
                            label=" Add new"
                            icon={plusIcon}
                            onClick={handleOpenAddPersona}
                        />
                    </div> }

                    {/* Main Content - Persona Table */}
                    <div className="mainContent">
                        <PersonaTable
                            data={filteredPersonaAccess}
                            onEditPersona={handleEditPersonaOpen}
                            onDeletePersona={handleDeletePersona}
                            isSuperAdmin={isSuperAdmin}
                        />
                    </div>

                    {/* Add/Edit Persona Popup */}
                    <AddAndEditPopup
                        open={openAddPersona}
                        onClose={handleCloseAddPersona}
                        onSubmit={handleSavePersona}
                        type={actionType}
                        selectedPersonaData={selectedPersonaData}
                    />

                    {/* Delete Confirmation Popup */}
                    <DeletePopup
                        open={singleDeletePopupOpen}
                        selectedUsers={selectedPersona}
                        onClose={() => {
                            setSingleDeletePopupOpen(false);
                            setSelectedPersona(null);
                            setSelectedPersonaData(null);
                        }}
                        onConfirm={handleDeleteConfirm}
                        type="persona"
                    />

                    {/* SnackBar Component */}
                    <SnackBarMessage
                        type={snackBarObject.type}
                        snackbarMessage={snackBarObject.message}
                        snackbarOpen={snackBarObject.snackbarOpen}
                        setSnackbarOpen={closeSnackbar}
                    />
                </>
            )}
        </div>
    );
};

export default Persona;